import { Component, computed, inject, input, linkedSignal, signal } from "@angular/core";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { TranslatePipe, TranslateService } from "@ngx-translate/core";
import { Parser } from "expr-eval-fork";

import { AdaaHelper } from "../../../../../../../../core/utils";
import { Constants } from "../../../../../../../constants/constants";
import { PageMode } from "../../../../../../../constants/enums";
import { CardEntryInfoType } from "../../../../../../../models";
import { DataentryApiService, LanguageService } from "../../../../../../../services";
import { EditDataEntryModalComponent } from "../../../../../..";
import { haveAikpiItemsChanged } from "../aikpi-items-diff/aikpi-items-diff";
import { AikpiItemsViewComponent } from "../aikpi-items-diff/aikpi-items-view";
import { AikpiPeriodToggleComponent } from "../aikpi-items-diff/aikpi-period-toggle";
import type { BoundedFormulaMetricType, FormulaMetricType, MetricType, UnboundedFormulaMetricType } from "../types";

@Component({
  selector: "adaa-formula-view",
  styleUrl: "../styles.scss",
  imports: [TranslatePipe, AikpiItemsViewComponent, AikpiPeriodToggleComponent],
  templateUrl: "./formula-view.html",
})
export class FormulaViewComponent {
  private _modalService = inject(NgbModal);
  private _translateService = inject(TranslateService);
  private _languageService = inject(LanguageService);
  private _dataentryApiService = inject(DataentryApiService);

  formula = input<string>();
  isBounded = input<boolean>();
  showOnlyNew = input<boolean>();
  baselineYear = input<number>();
  isBaselineKpi = input<boolean>();
  newDataentryRequests = input<FormulaMetricType[]>([]);
  oldDataentryRequests = input<FormulaMetricType[]>([]);
  processedDataEntryRequests = signal<FormulaMetricType[]>([]);
  isForecastedValues = input<boolean>(false);
  isItemsEntry = input<boolean>(false);
  isAuditTrail = input<boolean>(false);
  private _parser = new Parser();

  metricList = computed(() => {
    let metricList: FormulaMetricType["metrics"] = [];
    let maxSize = -1;
    for (const metric of this.newDataentryRequests()) {
      if (!metric.metrics || metric.metrics.length < 1) continue;
      if (metric.metrics.length > maxSize) {
        maxSize = metric.metrics.length;
        metricList = metric.metrics;
      }
    }

    return metricList.reduce<FormulaMetricType["metrics"]>((acc, curr) => {
      const metric = acc?.find(({ kpiMetricId }) => kpiMetricId === curr.kpiMetricId);
      if (!metric) acc?.push(curr);
      return acc;
    }, []);
  });

  frequency = computed(() => {
    for (const metric of this.newDataentryRequests()) {
      if (!metric.metrics || metric.metrics.length < 1) continue;
      const $m = metric.metrics[0];
      if ($m.frequency) {
        return $m.frequency;
      }
    }
    return 0;
  });

  metricItems = computed(() =>
    this.processedDataEntryRequests().map((request) =>
      (request.metrics ?? []).map((metric) => {
        const oldItems = this._getOldMetric(request, metric)?.operationStrategicTasks ?? [];
        const newItems = this._getNewItems(metric, oldItems);

        return { newItems, oldItems, hasDiff: haveAikpiItemsChanged(newItems, oldItems) };
      })
    )
  );

  itemsRows = computed(() =>
    this.processedDataEntryRequests().map((request, requestIndex) =>
      (request.metrics ?? [])
        .map((metric, metricIndex) => ({ metric, items: this.metricItems()[requestIndex][metricIndex] }))
        .filter(({ metric }) => metric.actual != null || metric.previous != null)
    )
  );

  expandedPeriods = linkedSignal<ReadonlySet<number>>(
    () =>
      new Set(
        this.processedDataEntryRequests().flatMap((request, requestIndex) =>
          this._hasPeriodChanges(request, requestIndex) ? [requestIndex] : []
        )
      )
  );

  columnCount = computed(() => {
    const columns = 2 + (this.metricList() ?? []).length * 2;
    if (this.isForecastedValues()) return columns;

    const evidenceColumn = 1;
    const targetColumns = this.isBounded() ? 2 : 1;
    return columns + evidenceColumn + targetColumns;
  });

  public ngOnInit() {
    this._processData();
  }

  public getUnboundedValue(metric: FormulaMetricType) {
    return this._getBaselineValue(metric.year, (metric as UnboundedFormulaMetricType).target);
  }

  public getBoundedValue(type: "lower" | "high", metric: FormulaMetricType) {
    let target: number;
    switch (type) {
      case "lower": {
        target = (metric as BoundedFormulaMetricType).lowerLimit;
        break;
      }

      case "high": {
        target = (metric as BoundedFormulaMetricType).highLimit;
        break;
      }
    }

    return this._getBaselineValue(metric.year, target);
  }

  public getCalculation(requestIndex: number) {
    const requests = this.newDataentryRequests();
    const metrics = this.metricList() ?? [];
    if (!AdaaHelper.isDefined(requests)) return;

    const request = requests[requestIndex];
    const nullValues = (request.metrics ?? []).filter((m) => !AdaaHelper.isDefined(m?.actual)).length;
    if (nullValues > 0) return;

    const scope = request.metrics?.reduce((acc, curr) => {
      if (curr.metricNameAE in acc) return acc;
      return {
        ...acc,
        [curr.metricNameAE]: curr.actual,
      };
    }, {});

    if (Object.keys(scope!).length !== metrics.length) return;

    try {
      const formula = this.formula();
      if (!formula) return "";

      const expr = this._parser.parse(formula);
      return AdaaHelper.roundValue(expr.evaluate(scope), 2, "");
    } catch (_e) {
      return "";
    }
  }

  public showNAFlag(requestIndex: number, metricIndex: number, version: "old" | "new") {
    const requests = version === "old" ? this.oldDataentryRequests() : this.newDataentryRequests();
    if (requests == null) return false;

    const request = requests[requestIndex];

    const metric = (request?.metrics ?? []).find((_, index: number) => metricIndex == index);
    if (!metric) return false;

    return metric.ignored;
  }

  public getPeriodLabel(metric: FormulaMetricType) {
    if (this.frequency() === Constants.FREQUENCY.MONTHLY) {
      return `${AdaaHelper.getMonthName(metric.month, AdaaHelper.getCurrentLang(), false)} ${metric.year}`;
    }

    if (this.frequency() === Constants.FREQUENCY.QUARTERLY) {
      return `${metric.year} ${this._translateService.instant(`data_entry.q${metric.quarter}`)}`;
    }

    if (this.frequency() === Constants.FREQUENCY.SEMIANNUAL) {
      return `${this._translateService.instant(`targets.periods.semestral.${metric.semester}`)} ${metric.year}`;
    }

    return metric.year || metric.metrics?.[0].year;
  }

  public viewDifferences(metric: FormulaMetricType) {
    this._dataentryApiService.getAnalysisDifferencesInfo(metric.itemId, metric.periodId, metric.itemType).subscribe({
      next: (response) => this._openEditDataEntryModal(this.getPeriodLabel(metric), response.responseData),
    });
  }

  public getMetricTitle(metric: MetricType) {
    const description = AdaaHelper.getItemValueByToken(metric, "metricDsc", this._languageService.current());
    if (!description) return metric.metricNameAE;

    return `${metric.metricNameAE} - ${description}`;
  }

  public togglePeriod(index: number) {
    this.expandedPeriods.update((expanded) => {
      const next = new Set(expanded);
      if (next.has(index)) {
        next.delete(index);
        return next;
      }

      next.add(index);
      return next;
    });
  }

  private _getOldMetric(request: FormulaMetricType, metric: MetricType) {
    const periodId = this._getPeriodId(request);
    const oldRequest = (this.oldDataentryRequests() ?? []).find((old) => this._getPeriodId(old) === periodId);

    return oldRequest?.metrics?.find(({ kpiMetricId }) => kpiMetricId === metric.kpiMetricId);
  }

  private _hasPeriodChanges(request: FormulaMetricType, requestIndex: number) {
    const items = this.metricItems()[requestIndex] ?? [];

    return (request.metrics ?? []).some((metric, metricIndex) => {
      if (metric.hasDiff || items[metricIndex]?.hasDiff) return true;

      return (metric.actual ?? null) !== (this._getOldMetric(request, metric)?.actual ?? null);
    });
  }

  private _getPeriodId(request: FormulaMetricType) {
    return request.periodId ?? request.metrics?.[0]?.periodId;
  }

  private _getNewItems(metric: MetricType, oldItems: MetricType["operationStrategicTasks"]) {
    if (this.isAuditTrail() || metric.wfProcessCtlId) return metric.operationStrategicTasks ?? [];

    return oldItems ?? [];
  }

  private _openEditDataEntryModal(periodLabel: string | number | undefined, dataEntry: CardEntryInfoType): void {
    const modal = this._modalService.open(EditDataEntryModalComponent, {
      centered: true,
      size: "lg",
      modalDialogClass: this._languageService.modalDirection(),
    });

    modal.componentInstance.header = `${this._translateService.instant("data_entry.period")}: ${periodLabel}`;
    modal.componentInstance.dataEntry = dataEntry;
    modal.componentInstance.pageMode = PageMode.view;
    modal.componentInstance.showSubmit = false;
  }

  private _getBaselineValue(year: number, value: number) {
    if (year === this.baselineYear() && this.isBaselineKpi()) {
      return this._translateService.instant("kpi.baselineYear");
    }
    return value;
  }

  private _processData() {
    if (!this.isForecastedValues()) {
      this.processedDataEntryRequests.set(this.newDataentryRequests());
      return;
    }

    const newData = this.newDataentryRequests() ?? [];
    const oldData = this.oldDataentryRequests() ?? [];
    const metricsCount = this.metricList()?.length ?? 0;

    for (let index = 0; index < newData.length; index++) {
      const newItem = newData[index];
      for (let i = 0; i < metricsCount; i++) {
        const oldItem = oldData.find((data) => data.metrics?.[i]?.id === newItem.metrics?.[i]?.kpiMetricId);
        const newMetric = newItem.metrics?.[i];
        const oldMetric = oldItem?.metrics?.[i];
        let hasDiff: boolean;
        if (!newMetric?.actual && !oldMetric?.actual) {
          hasDiff = false;
        } else {
          hasDiff = oldMetric ? newMetric?.actual !== oldMetric?.actual : true;
        }

        newMetric!.hasDiff = hasDiff;
        oldMetric?.actual && (newMetric!.previous = oldMetric?.actual);
        newItem.metrics![i] = newMetric!;
        newData[index] = newItem;
      }
    }

    this.processedDataEntryRequests.set(newData);
  }
}
